// @module:knowledge-base
/**
 * Derives a human-readable, granular processing status for a KnowledgeObject.
 *
 * The pipeline progresses through five stages:
 *   1. Pending             — uploaded, waiting for Docling conversion
 *   2. Processing – L1     — Docling conversion is running (unit extraction)
 *   3. Processing – L2     — conversion done, L2 summaries not yet generated
 *   4. Processing – L3     — L2 done, L3 extractions not yet generated
 *   5. Complete            — all three layers exist
 *
 * "failed" is preserved as-is from the underlying conversion_status.
 */

import type { ConversionStatus } from '../types/knowledge-base';

export type KODisplayStatus =
  | 'pending'
  | 'processing_l1'
  | 'processing_l2'
  | 'processing_l3'
  | 'complete'
  | 'failed';

export interface KOStatusInfo {
  /** Machine-readable status key */
  status: KODisplayStatus;
  /** Short label shown in tags / badges */
  label: string;
  /** Carbon Tag `type` prop value */
  tagType: 'gray' | 'blue' | 'cyan' | 'purple' | 'green' | 'red';
}

/**
 * Derive the display status of a KnowledgeObject from its persistence fields.
 *
 * @param conversionStatus  The raw DB `conversion_status` value.
 * @param l1Count           Number of L1 knowledge units (`knowledge_unit_count`).
 * @param l2Count           Number of L2 summaries (`summary_count`).
 * @param l3Count           Number of L3 extractions (`extraction_count`).
 * @param layersStatus      The raw DB `layers_status` value (L2/L3 pipeline state, set by
 *                          `KnowledgeLayerService.processLayers()` itself). `undefined`/`null`
 *                          for callers that haven't been updated to pass it yet — falls back to
 *                          the count-based inference below so this stays backward-compatible.
 */
export function deriveKOStatus(
  conversionStatus: ConversionStatus,
  l1Count: number,
  _l2Count: number,
  _l3Count: number,
  layersStatus?: string | null
): KOStatusInfo {
  if (conversionStatus === 'failed') {
    return { status: 'failed', label: 'Failed', tagType: 'red' };
  }

  if (conversionStatus === 'pending') {
    return { status: 'pending', label: 'Pending', tagType: 'gray' };
  }

  // Docling conversion is running — L1 unit extraction in progress.
  if (conversionStatus === 'processing') {
    return { status: 'processing_l1', label: 'Processing - L1 - Unit Extraction', tagType: 'blue' };
  }

  // conversion_status === 'completed' from here on.

  // L1 units not yet generated (rechunk hasn't run, or still in progress).
  if (l1Count === 0) {
    return { status: 'processing_l1', label: 'Processing - L1 - Unit Extraction', tagType: 'blue' };
  }

  // The L2/L3 pipeline explicitly reports failure — surface it rather than the generic
  // "Complete", since L1 succeeding does not mean processLayers did.
  if (layersStatus === 'failed') {
    return { status: 'failed', label: 'Failed', tagType: 'red' };
  }

  // L2/L3 is queued ('pending') or actively running ('processing'). Both show the same
  // enrichment badge: 'pending' means the Kafka consumer has not yet claimed the document
  // (claim() is the only writer of 'processing'), and 'processing' means it is actively
  // running. Showing "Complete" while pending would hide the upcoming enrichment pass.
  if (layersStatus === 'pending' || layersStatus === 'processing') {
    return { status: 'processing_l3', label: 'Processing - L2/L3 - Enrichment', tagType: 'purple' };
  }

  // Conversion completed + L1 units present → the document is fully processed and
  // usable (chunked + embedded, groundable in an exploration). L2 summaries are
  // only created for chunks over the ~3000-char embedding threshold, and L3
  // extractions only exist for image/table units — both legitimately stay at 0
  // for short, text-only documents. They are optional enrichment (surfaced as
  // the L1/L2/L3 count badges), so they must NOT hold the document in a
  // perpetual "Processing" state.
  return { status: 'complete', label: 'Complete', tagType: 'green' };
}

// Made with Bob
